/*  MUI  */
import { Box, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { TableCell } from '@mui/material';

/*  Shared  */
import { SendEmail } from 'application/Shared/Components/Buttons';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, SessionHelper } from 'application/Shared/Helpers';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Actions = ({ importUser, row, showImportModal }) => {
    const { t } = useTranslation();
    const session = useSession();
    const [showInviteModal, setShowInviteModal] = useState(false);

    const sessionHelper = new SessionHelper(session);
    const { id, email, loading } = row;

    const options = [
        {
            hide: loading,
            key: 'import',
            label: t('Common.importUser'),
            value: 'import'
        },
        {
            hide: !(Helpers.isEmail(email) && !sessionHelper.isANM()),
            key: 'sendEmail',
            label: t('Users.invite'),
            value: 'sendEmail'
        }
    ];

    const onClick = (value) => {
        if (value === 'import') {
            showImportModal(true, row);
        }
        if (value === 'sendEmail') {
            setShowInviteModal(true);
        }
    };

    return (
        <>
            <KebabMenu
                options={options}
                onClick={onClick}
                data-testid="providerUsersTableActions"
            />
            {showInviteModal && (
                <SendEmail
                    onClose={() => setShowInviteModal(false)}
                    successFn={importUser}
                    successParam={id}
                    to={email}
                />
            )}
        </>
    );
};

const RenderRow = ({
    importUser,
    row,
    showImportModal,
    isMicrosoftEntraID
}) => {
    const { t } = useTranslation();
    const {
        displayName,
        email,
        userPrincipalName,
        onPremisesUserPrincipalName,
        userType,
        exists,
        id
    } = row;

    // Microsoft Entra ID uses userPrincipalName
    const emailToShow = isMicrosoftEntraID ? userPrincipalName : email;

    const iconVariant = exists ? 'success' : 'danger';
    const iconSrc = exists ? 'Check' : 'Close';
    const translatedStatus = t(`Common.${exists?.disabled ? 'yes' : 'no'}`);

    return (
        <>
            <TableCell>
                {displayName}
                <br />
                <small>{id}</small>
            </TableCell>
            <TableCell>{emailToShow}</TableCell>
            {isMicrosoftEntraID && (
                <TableCell>{onPremisesUserPrincipalName || '-'}</TableCell>
            )}
            {isMicrosoftEntraID && <TableCell>{userType}</TableCell>}
            <TableCell>
                <IconWithVariant
                    variant={iconVariant}
                    src={iconSrc}
                    size={FontSizes[2]}
                />
            </TableCell>
            <TableCell>
                {exists ? <Box>{translatedStatus}</Box> : '-'}
            </TableCell>
            <TableCell align="right">
                {!exists && (
                    <Actions
                        importUser={importUser}
                        row={row}
                        showImportModal={showImportModal}
                    />
                )}
            </TableCell>
        </>
    );
};

const csvFields = ({ isMicrosoftEntraID, t, translationProvider }) => {
    const fields = [
        { key: 'id', label: t('Provider.identityMgrID') },
        { key: 'displayName', label: t('Common.name') }
    ];
    if (!isMicrosoftEntraID) {
        fields.push({ key: 'email', label: t('Common.email') });
    }
    if (isMicrosoftEntraID) {
        fields.push(
            { key: 'userPrincipalName', label: t('Common.email') },
            {
                key: 'onPremisesUserPrincipalName',
                label: t(
                    `Provider.${translationProvider}.on_premises_user_principal_name`
                )
            },
            {
                key: 'userType',
                label: t(`Provider.${translationProvider}.user_type`)
            }
        );
    }
    fields.push({ key: 'imported', label: t('Common.imported') });
    return fields;
};

const headers = ({ isMicrosoftEntraID, t, translationProvider }) => {
    const emailWidth = isMicrosoftEntraID ? '25%' : '45%';
    const hdrs = [
        {
            align: 'left',
            field: 'user',
            renderHeader: <strong>{t('Common.user')}</strong>,
            sortable: true,
            sortableFieldType: 'string',
            sortableKey: 'displayName'
        },
        {
            align: 'left',
            field: 'email',
            renderHeader: <strong>{t('Common.email')}</strong>,
            sortable: true,
            sortableFieldType: 'string',
            sortableKey: isMicrosoftEntraID ? 'userPrincipalName' : 'email',
            width: `${emailWidth}`
        }
    ];
    if (isMicrosoftEntraID) {
        hdrs.push(
            {
                align: 'left',
                field: 'onPremisesUserPrincipalName',
                renderHeader: (
                    <strong>
                        {t(
                            `Provider.${translationProvider}.on_premises_user_principal_name`
                        )}
                    </strong>
                ),
                sortable: true,
                sortableFieldType: 'string',
                sortableKey: 'onPremisesUserPrincipalName',
                width: '25%'
            },
            {
                align: 'left',
                field: 'userType',
                renderHeader: (
                    <strong>
                        {t(`Provider.${translationProvider}.user_type`)}
                    </strong>
                ),
                sortable: true,
                sortableFieldType: 'string',
                sortableKey: 'userType',
                width: '10%'
            }
        );
    }
    hdrs.push({
        field: null,
        renderHeader: <strong>{t('Common.imported')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'exists',
        width: '5%'
    });
    hdrs.push({
        field: 'exists.disabled',
        renderHeader: <strong>{t('Common.disabled')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'exists.disabled',
        width: '5%'
    });
    hdrs.push({
        align: 'right',
        field: null,
        renderHeader: <strong>{t('Common.actions')}</strong>,
        width: '5%'
    });
    return hdrs;
};

const searchFields = [
    'displayName',
    'userPrincipalName',
    'email',
    'onPremisesUserPrincipalName',
    'userType',
    'id'
];

export const ProvidersUsersTable = ({
    importUser,
    loading,
    rerenderHeaders,
    rowMap,
    showImportModal,
    translationProvider,
    users
}) => {
    const { t } = useTranslation();
    const csvData = Object.values(rowMap);

    const isMicrosoftEntraID = translationProvider === 'MicrosoftConfiguration';
    const filename = isMicrosoftEntraID
        ? 'microsoft_entra_id_users'
        : 'gws_users';

    const tableCsvFields = useMemo(
        () => csvFields({ isMicrosoftEntraID, t, translationProvider }),
        [isMicrosoftEntraID, t, translationProvider]
    );

    const tableHeaders = useMemo(
        () => headers({ isMicrosoftEntraID, t, translationProvider }),
        [isMicrosoftEntraID, t, translationProvider]
    );

    return (
        <ManagedTable
            csvData={csvData}
            csvExport
            csvExportFields={tableCsvFields}
            csvExportFileName={filename}
            externalLoading={loading}
            headers={tableHeaders}
            renderBody={(row) => (
                <RenderRow
                    importUser={importUser}
                    row={row}
                    showImportModal={showImportModal}
                    isMicrosoftEntraID={isMicrosoftEntraID}
                />
            )}
            renderBodyKey="id"
            renderFooter
            rerenderHeaders={rerenderHeaders}
            rows={users}
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="displayName"
        />
    );
};
